
import React, { useState, useEffect } from "react";
import { FaEnvelope, FaMapMarkerAlt, FaPhone, FaClock, FaPaperPlane, FaUser, FaGlobeAfrica, FaUsers, FaCalendarAlt } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import "../css/styles.css";
import contactImage from "../img/ser.jpg";
import { API_BASE, parseJsonResponse } from "../api";

const Contact = () => {
  const location = useLocation();
  const safariParam = new URLSearchParams(location.search).get("safari") || "";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    destination: "",
    travelers: "",
    travelDate: "",
    budget: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (safariParam) {
      setFormData((prev) => ({
        ...prev,
        message: `I'm interested in: ${safariParam}`,
      }));
    }
  }, [safariParam]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          destination: formData.destination,
          travelers: formData.travelers,
          date: formData.travelDate,
          budget: formData.budget,
          message: formData.message,
        }),
      });
      const data = await parseJsonResponse(res);
      if (!res.ok) {
        throw new Error(data.error || data.message || `Request failed ${res.status}`);
      }
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", destination: "", travelers: "", travelDate: "", budget: "", message: "" });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Banner */}
      <div className="contact-hero">
        <img src={contactImage} alt="Contact Us" className="contact-hero-img" />
        <div className="contact-hero-overlay">
          <h1>Get In Touch</h1>
          <p>Let us craft your perfect African adventure</p>
        </div>
      </div>

      {/* Info Cards */}
      <div className="contact-info-grid">
        <div className="contact-info-card">
          <div className="contact-info-icon"><FaMapMarkerAlt /></div>
          <h3>Visit Us</h3>
          <p>Riruta, Nairobi, Kenya</p>
        </div>
        <div className="contact-info-card">
          <div className="contact-info-icon"><FaEnvelope /></div>
          <h3>Email Us</h3>
          <p>info@nyatwende.com</p>
          <p>bookings@nyatwende.com</p>
        </div>
        <div className="contact-info-card">
          <div className="contact-info-icon"><FaPhone /></div>
          <h3>Call Us</h3>
          <p>+254 706 136 146</p>
          <p>+254 798 178 735</p>
        </div>
        <div className="contact-info-card">
          <div className="contact-info-icon"><FaClock /></div>
          <h3>Hours</h3>
          <p>Mon-Fri: 9AM - 6PM</p>
          <p>Sat: 10AM - 5PM</p>
        </div>
      </div>

      {/* Modern Form Section */}
      <section className="modern-form-section contact-form-section">
        <div className="modern-form-glow modern-form-glow-1" />
        <div className="modern-form-glow modern-form-glow-2" />
        <div className="modern-form-wrapper">
          <div className="modern-form-header">
            <span className="modern-form-eyebrow">Send Us a Message</span>
            <h2>Plan Your Dream Safari</h2>
            <p>Fill in the details below and our travel experts will get back to you within 24 hours.</p>
          </div>

          {error && (
            <div className="modern-form-error">
              <p>{error}</p>
            </div>
          )}

          {submitted ? (
            <div className="modern-form-success">
              <div className="success-checkmark">&#10003;</div>
              <h3>Message Sent!</h3>
              <p>Our team will reach out to you shortly.</p>
            </div>
          ) : (
            <form className="modern-form" onSubmit={handleSubmit}>
              <div className="modern-form-grid">
                <div className="modern-form-group">
                  <label htmlFor="contact-name">
                    <FaUser className="field-icon" /> Full Name
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="modern-form-group">
                  <label htmlFor="contact-email">
                    <FaEnvelope className="field-icon" /> Email Address
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="modern-form-group">
                  <label htmlFor="contact-phone">
                    <FaPhone className="field-icon" /> Phone Number
                  </label>
                  <input
                    id="contact-phone"
                    type="tel"
                    name="phone"
                    placeholder="+254 700 000 000"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="modern-form-group">
                  <label htmlFor="contact-destination">
                    <FaGlobeAfrica className="field-icon" /> Preferred Destination
                  </label>
                  <select
                    id="contact-destination"
                    name="destination"
                    value={formData.destination}
                    onChange={handleChange}
                  >
                    <option value="">Select a destination</option>
                    <option value="masai-mara">Masai Mara, Kenya</option>
                    <option value="amboseli">Amboseli, Kenya</option>
                    <option value="kilimanjaro">Kilimanjaro, Tanzania</option>
                    <option value="serengeti">Serengeti, Tanzania</option>
                    <option value="zanzibar">Zanzibar</option>
                    <option value="kenya-tanzania">Kenya & Tanzania Combo</option>
                    <option value="other">Other / Not Sure</option>
                  </select>
                </div>
                <div className="modern-form-group">
                  <label htmlFor="contact-travelers">
                    <FaUsers className="field-icon" /> Number of Travelers
                  </label>
                  <select
                    id="contact-travelers"
                    name="travelers"
                    value={formData.travelers}
                    onChange={handleChange}
                  >
                    <option value="">How many travelers?</option>
                    <option value="1">Solo Traveler</option>
                    <option value="2">2 Travelers</option>
                    <option value="3-5">3 - 5 Travelers</option>
                    <option value="6-10">6 - 10 Travelers</option>
                    <option value="10+">10+ (Group)</option>
                  </select>
                </div>
                <div className="modern-form-group">
                  <label htmlFor="contact-date">
                    <FaCalendarAlt className="field-icon" /> Preferred Travel Date
                  </label>
                  <input
                    id="contact-date"
                    type="date"
                    name="travelDate"
                    value={formData.travelDate}
                    onChange={handleChange}
                  />
                </div>
                <div className="modern-form-group">
                  <label htmlFor="contact-budget">Budget Range (USD)</label>
                  <select
                    id="contact-budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                  >
                    <option value="">Select budget range</option>
                    <option value="under-1000">Under $1,000</option>
                    <option value="1000-3000">$1,000 - $3,000</option>
                    <option value="3000-5000">$3,000 - $5,000</option>
                    <option value="5000-10000">$5,000 - $10,000</option>
                    <option value="10000+">$10,000+</option>
                  </select>
                </div>
              </div>
              <div className="modern-form-group full-width">
                <label htmlFor="contact-message">Your Message</label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows="5"
                  placeholder="Tell us about your dream safari... any special requests, interests, or questions?"
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
              </div>
              <button type="submit" className="modern-form-submit" disabled={loading}>
                {loading ? (
                  <span className="spinner-wrapper"><span className="spinner" /> Sending...</span>
                ) : (
                  <><FaPaperPlane /> Send Message</>
                )}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Contact;
